// src/utils/systemMessageGenerator.ts
import { Persona } from '../types';

/**
 * Capitalizes the first character of a string
 */
export function capitalizeFirstChar(str: string): string {
	if (!str || str.length === 0) return str;
	return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Generates a comprehensive system message for bootstrap_version_3 personas
 * using all available demographic and personality data
 */
export function generateSystemMessage(persona: Persona): string {
	// For non-v3 personas, return existing system message or generate basic one
	if (persona.bootstrap_version !== '3') {
		return persona.systemMessage || persona.desc || 'You are a helpful assistant.';
	}

	const parts: string[] = [];

	// Strong identity statement - you ARE this person
	const personaName = persona.name || 'this persona';
	parts.push(`You ARE ${personaName}. You live as this person completely and authentically.`);
	
	// Base persona description with emphasis
	if (persona.desc) {
		parts.push(`Your core identity: ${persona.desc}.`);
	}

	// Demographics and background
	const demographics: string[] = [];
	if (persona.age) {
		demographics.push(`${persona.age} years old`);
	}
	if (persona.sex) {
		demographics.push(persona.sex.toLowerCase());
	}
	if (persona.marital_status) {
		demographics.push(persona.marital_status.toLowerCase());
	}
	if (demographics.length > 0) {
		parts.push(`You are ${demographics.join(', ')}.`);
	}

	// Location (comprehensive)
	const location: string[] = [];
	if (persona.city) location.push(persona.city);
	if (persona.state) location.push(persona.state);
	if (persona.zipcode) location.push(persona.zipcode);
	if (persona.country) location.push(persona.country);
	if (location.length > 0) {
		parts.push(`You live in ${location.join(', ')}. This is your home and you know this area intimately.`);
	}

	// Education and occupation
	if (persona.education_level) {
		let education = `You have a ${persona.education_level.toLowerCase()} education`;
		if (persona.bachelors_field) {
			education += ` with a degree in ${persona.bachelors_field}`;
		}
		education += '.';
		parts.push(education);
	}

	if (persona.occupation) {
		parts.push(`You work as a ${persona.occupation.toLowerCase()}.`);
	}

	// Professional persona
	if (persona.professional_persona) {
		parts.push(`Your professional identity: ${persona.professional_persona}. This defines how you approach work and professional relationships.`);
	}

	// Skills and expertise (comprehensive)
	if (persona.skills_and_expertise) {
		parts.push(`Your skills and expertise include: ${persona.skills_and_expertise}.`);
	}
	if (persona.skills_and_expertise_list && persona.skills_and_expertise_list.trim() !== '') {
		parts.push(`Detailed skills: ${persona.skills_and_expertise_list}.`);
	}

	// Career goals
	if (persona.career_goals_and_ambitions) {
		parts.push(`Your personal career goals and ambitions: ${persona.career_goals_and_ambitions}. These drive your professional decisions and aspirations.`);
	}

	// Personal interests and hobbies (comprehensive)
	const interests: string[] = [];
	if (persona.hobbies_and_interests) {
		interests.push(`hobbies include ${persona.hobbies_and_interests}`);
	}
	if (persona.hobbies_and_interests_list && persona.hobbies_and_interests_list.trim() !== '') {
		interests.push(`specific interests: ${persona.hobbies_and_interests_list}`);
	}

	// Specific persona aspects - these define your personality
	const personalityAspects: string[] = [];
	if (persona.sports_persona) {
		personalityAspects.push(`Sports personality: ${persona.sports_persona}. This shapes your approach to fitness, competition, and teamwork`);
	}
	if (persona.arts_persona) {
		personalityAspects.push(`Arts personality: ${persona.arts_persona}. This influences your creative expression and aesthetic preferences`);
	}
	if (persona.travel_persona) {
		personalityAspects.push(`Travel personality: ${persona.travel_persona}. This defines your wanderlust and cultural curiosity`);
	}
	if (persona.culinary_persona) {
		personalityAspects.push(`Culinary personality: ${persona.culinary_persona}. This shapes your relationship with food and cooking`);
	}

	// Add personality aspects as separate detailed sections
	for (const aspect of personalityAspects) {
		parts.push(aspect + '.');
	}

	if (interests.length > 0) {
		parts.push(`Your personal ${interests.join(' and ')}.`);
	}

	// Complete persona embodiment instructions
	parts.push(`You must live as ${personaName} in every response. Think, feel, and react as this specific person would.`);
	parts.push('Draw from YOUR personal experiences, memories, background, and life circumstances when responding.');
	parts.push('Maintain complete consistency with your demographic profile, education, occupation, and personality throughout the conversation.');
	parts.push('Your responses should reflect your unique perspective based on your age, location, professional experience, and personal interests.');
	parts.push('Be authentically yourself - this specific person - while being helpful and engaging.');

	return parts.join(' ');
}

/**
 * Generates a shorter system message for UI display purposes
 */
export function generateShortSystemMessage(persona: Persona): string {
	if (persona.bootstrap_version !== '3') {
		return persona.systemMessage || persona.desc || 'Assistant';
	}

	const parts: string[] = [];

	if (persona.name) {
		parts.push(persona.name);
	}

	const demographics: string[] = [];
	if (persona.age) demographics.push(`${persona.age}yo`);
	if (persona.occupation) demographics.push(persona.occupation);
	if (persona.city && persona.state) {
		demographics.push(`${persona.city}, ${persona.state}`);
	}

	if (demographics.length > 0) {
		parts.push(`(${demographics.join(', ')})`);
	}

	return parts.join(' ') || 'Assistant';
}